import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'
import { cn } from '@app/lib/cn'

const buttonVariants = cva(
  [
    'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-xs border',
    'font-medium transition-colors',
    'disabled:pointer-events-none disabled:opacity-45',
    '[&_svg]:size-3.5 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'border-kordovan bg-kordovan text-kagit hover:bg-kordovan/88',
        outline: 'border-hairline bg-surface text-ink hover:border-ink-muted/45 hover:bg-kagit',
        ghost: 'border-transparent text-ink-muted hover:bg-kordovan/6 hover:text-ink',
        link: 'h-auto border-transparent p-0 text-kordovan underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2 text-micro',
        md: 'h-8 px-3 text-data',
        icon: 'size-8 p-0',
      },
    },
    defaultVariants: {
      variant: 'outline',
      size: 'md',
    },
  },
)

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child element instead of a <button> (links, router <Link>). */
    asChild?: boolean
  }

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />
}

export { buttonVariants }
